import { I18nType } from "./i18n-type";
import { LogService } from "..";
import availableLocales from "../../../tmp/availableLocales.json";
import { getAssetPath } from "@stencil/core";

export interface Locale {
	locale: string;
	label: string;
	labelEnglish: string;
}

class I18nServiceController {
	constructor() {}

	/**
	 * 1. Get user-defined locale from configuration
	 * 2. Fallback to page language - if available, get the closest lang tag to determine the page default language.
	 * 3. Fallback to browser language
	 * 4. Fallback to default fallback
	 */
	getLocale(fallbackLocale: string, element?: HTMLElement, configuredLocale?: string): string {
		let closestElement: HTMLElement;
		let allLocaleCodes = this.getFilteredLocales().map(o => o.locale.toLowerCase());
		let resultLocale: string;
		let htmlLocale: string;
		let browserLocale: string;

		LogService.log.info("Configured language:", configuredLocale);
		LogService.log.debug("All (available, filtered and sorted) locale codes:", allLocaleCodes);

		if (element) {
			closestElement = element.closest("[lang]") as HTMLElement;
		}

		if (closestElement) {
			htmlLocale = closestElement.lang;
		}

		LogService.log.info("Default page language:", htmlLocale);

		browserLocale = window.navigator.language;
		LogService.log.info("Browser language:", browserLocale);

		resultLocale = configuredLocale || htmlLocale || browserLocale;
		if (resultLocale) {
			resultLocale = resultLocale.toLowerCase();

			// first try the given locale directly, e.g. "de-at"
			if (allLocaleCodes.includes(resultLocale)) {
				LogService.log.info("Use locale:", resultLocale);
				return resultLocale;
			}

			// secondly, find a locale only by the language
			if (!resultLocale.includes("-")) {
				const languageOnly = resultLocale.split("-")[0];
				for (let i in allLocaleCodes) {
					if (allLocaleCodes[i].split("-")[0] === languageOnly) {
						LogService.log.debug("Select locale by language prefix:", allLocaleCodes[i]);
						return allLocaleCodes[i];
					}
				}
			}
		}

		LogService.log.info("No configured, web page or browser language found - fallback to default locale:", fallbackLocale);
		return fallbackLocale;
	}

	/**
	 * Retrieves the requested language from the host.
	 * @param locale The locale identifier string (e.g. "en-us", "de-de" etc.)
	 * @param initialLoad Indicates if this is the first time a locale is getting fetched
	 */
	async fetchI18n(locale: string, initialLoad: boolean): Promise<I18nType> {
		LogService.log.debug("i18n service: Fetching i18n file:", locale);
		try {
			let result = await this.fetchForStatistics(`../assets/i18n/${locale}.json`, initialLoad);
			if (result.ok) return result.json() as unknown as I18nType;
			else throw new Error(`The locale file could not be requested: ${result.statusText.toString()} ${result.status.toString()}`);
		} catch (error) {
			throw new Error(error);
		}
	}

	/**
	 * Merge 2 Arrays of locale Objects and remove duplicates
	 * @param {array} localeArray1 - Array of the standard locales
	 * @param {array} localeArray2 - Array of new custom locales from the configuration
	 * @returns {array} An array of all locales without duplication
	 */
	private mergeLocaleArrays = (localeArray1: Locale[], localeArray2: Locale[]): Locale[] => {
		let mergedLocales = localeArray1;

		for (const customLocale of localeArray2) {
			const isInArray = localeArray1.map(standardLocale => standardLocale.locale.toLowerCase() === customLocale.locale.toLowerCase()).indexOf(true);

			if (isInArray < 0) {
				mergedLocales.push(customLocale);
			}
		}

		return mergedLocales;
	};

	/**
	 * @param {array} localesToShowInMenu - Array of the only locales that are shown to the user
	 * @param {array} customLocales - Array of new custom locales from the configuration. These will be merged with the standard locales from the list created on build.
	 * @returns {array} An array of optionally filtered locales sorted alphabetically by locale. When no argument is given the standard locales will be used.
	 */
	private getFilteredLocales = (localesToShowInMenu?: string[], customLocales?: Locale[]): Locale[] => {
		let available: Locale[];
		// ToDo: Remove one of the two following lines
		LogService.log.debug("Filtering locales... ", availableLocales);
		LogService.log.debug("Available locales:", availableLocales);
		LogService.log.debug("Custom locales:", customLocales);
		LogService.log.debug("Locales to show (filter):", localesToShowInMenu);

		available = customLocales ? this.mergeLocaleArrays(availableLocales, customLocales) : availableLocales;

		if (localesToShowInMenu) {
			LogService.log.debug("LOCALES TO SHOW!!!", localesToShowInMenu);
			let show = [];
			for (let i in available) {
				for (let j in localesToShowInMenu) {
					if (available[i].locale.toLowerCase() === localesToShowInMenu[j].toLowerCase()) {
						show.push({ locale: available[i].locale.toLowerCase(), label: available[i].label, labelEnglish: available[i].labelEnglish });
					}
				}
			}
			available = show;
		}

		LogService.log.debug("Done filtering! Available filtered locales (unsorted):", available);

		return available.sort((a, b) => a.locale.localeCompare(b.locale));
	};

	/**
	 * Fetches the "signal" and "i18n" requests with the necessary referrer policy and dock statistics url (dsu).
	 */
	async fetchForStatistics(requestUrl: string, initialLoad?: boolean): Promise<Response> {
		let urlParams = new URLSearchParams({ dsu: location.host + location.pathname });
		if (initialLoad !== undefined) {
			urlParams.append("initialLoad", initialLoad.toString());
		}
		return await fetch(getAssetPath(requestUrl) + "?" + urlParams, {
			referrerPolicy: "no-referrer-when-downgrade",
		});
	}
}

export const I18nService = new I18nServiceController();
